"use client";

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Plus, Ban, RotateCcw } from "lucide-react";
import { usePapers, useMethodPapers } from "@/hooks/usePaperCatalog";
import { catalogApi } from "@/lib/catalog-api.service";
import { ADMISSION_METHODS } from "@/lib/data-constants";
import { Toast } from "@/lib/utils";

/**
 * Admin editor for the hồ sơ catalog (create/rename/reorder/deactivate) and,
 * per diện xét tuyển, which of those hồ sơ it requires and which default to
 * checked.
 *
 * `ADMISSION_METHODS` supplies the list of diện xét tuyển — the ids and
 * names stay hardcoded on purpose (they arrive from upstream admissions data
 * and land in `Student.DTT`); only the mapping to papers is editable here.
 */
export default function PaperManagement() {
	const {
		papers,
		loading: papersLoading,
		error: papersError,
		refetch: refetchPapers,
	} = usePapers(true);
	const [creating, setCreating] = useState(false);
	const [newPaperName, setNewPaperName] = useState("");
	const [createError, setCreateError] = useState<string | null>(null);

	const [selectedMethodId, setSelectedMethodId] = useState<string>(ADMISSION_METHODS[0]?.id ?? "");
	const {
		methodPapers,
		setMethodPapers,
		loading: methodPapersLoading,
	} = useMethodPapers(selectedMethodId || undefined);
	const [mappingError, setMappingError] = useState<string | null>(null);

	const attachedPaperIds = useMemo(
		() => new Set(methodPapers.map((mp) => mp.paperId)),
		[methodPapers],
	);
	const activePapers = useMemo(() => papers.filter((p) => p.active), [papers]);

	const handleCreatePaper = async (event: React.FormEvent) => {
		event.preventDefault();
		setCreateError(null);
		try {
			await catalogApi.createPaper({ name: newPaperName.trim() });
			setNewPaperName("");
			setCreating(false);
			Toast.success("Đã thêm hồ sơ");
			await refetchPapers();
		} catch (error) {
			setCreateError(error instanceof Error ? error.message : "Không thể thêm hồ sơ");
		}
	};

	const handleRename = async (id: number, name: string) => {
		try {
			await catalogApi.updatePaper(id, { name });
			await refetchPapers();
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể đổi tên hồ sơ");
		}
	};

	const handleReorder = async (id: number, order: number) => {
		try {
			await catalogApi.updatePaper(id, { order });
			await refetchPapers();
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể đổi thứ tự hồ sơ");
		}
	};

	const handleToggleActive = async (id: number, active: boolean) => {
		try {
			if (active) {
				await catalogApi.updatePaper(id, { active: true });
			} else {
				await catalogApi.deletePaper(id);
			}
			await refetchPapers();
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể cập nhật trạng thái hồ sơ");
		}
	};

	/** The mapping endpoint replaces the whole list, so every edit resends it. */
	const saveMapping = async (
		next: Array<{ paperId: number; order: number; defaultChecked: boolean }>,
	) => {
		if (!selectedMethodId) return;
		setMappingError(null);
		try {
			// The PUT already answers with the mapping it just wrote, so re-reading
			// it would be a second round trip for data we are holding.
			setMethodPapers(await catalogApi.replaceMethodPapers(selectedMethodId, next));
		} catch (error) {
			setMappingError(
				error instanceof Error ? error.message : "Không thể cập nhật hồ sơ của diện này",
			);
		}
	};

	const handleAttachToggle = (paperId: number, attach: boolean) => {
		const current = methodPapers.map((mp) => ({
			paperId: mp.paperId,
			order: mp.order,
			defaultChecked: mp.defaultChecked,
		}));

		const next = attach
			? [...current, { paperId, order: current.length, defaultChecked: true }]
			: current.filter((mp) => mp.paperId !== paperId);

		saveMapping(next);
	};

	const handleDefaultCheckedToggle = (paperId: number, defaultChecked: boolean) => {
		const next = methodPapers.map((mp) =>
			mp.paperId === paperId
				? { paperId: mp.paperId, order: mp.order, defaultChecked }
				: { paperId: mp.paperId, order: mp.order, defaultChecked: mp.defaultChecked },
		);
		saveMapping(next);
	};

	return (
		<div className="space-y-8">
			{/* Paper catalog */}
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-semibold text-foreground">Danh mục hồ sơ</h3>
					<Button
						size="sm"
						onClick={() => setCreating((v) => !v)}
						className="flex items-center gap-2"
					>
						<Plus className="h-4 w-4" />
						Thêm hồ sơ
					</Button>
				</div>

				{creating && (
					<form onSubmit={handleCreatePaper} className="flex items-end gap-2">
						<div className="flex-1">
							<label className="block text-sm font-medium text-muted-foreground mb-1">
								Tên hồ sơ
							</label>
							<Input
								value={newPaperName}
								onChange={(e) => setNewPaperName(e.target.value)}
								placeholder="Nhập tên hồ sơ"
								required
							/>
						</div>
						<Button type="submit">Lưu</Button>
						<Button type="button" variant="outline" onClick={() => setCreating(false)}>
							Hủy
						</Button>
					</form>
				)}
				{createError && <p className="text-sm text-red-600 dark:text-red-400">{createError}</p>}

				{papersError && <p className="text-sm text-red-600 dark:text-red-400">{papersError}</p>}
				{papersLoading ? (
					<div className="space-y-2">
						{Array.from({ length: 4 }).map((_, i) => (
							<Card key={i}>
								<CardContent className="p-3 flex items-center gap-3">
									<Skeleton className="h-9 flex-1" />
									<Skeleton className="h-8 w-20" />
								</CardContent>
							</Card>
						))}
					</div>
				) : (
					<div className="space-y-2">
						{papers.map((paper) => (
							<Card key={paper.id} className={!paper.active ? "opacity-50 bg-muted" : ""}>
								<CardContent className="p-3 flex items-center gap-3">
									<Input
										defaultValue={paper.name}
										className="flex-1"
										disabled={!paper.active}
										onBlur={(e) => {
											const name = e.target.value.trim();
											if (name && name !== paper.name) handleRename(paper.id, name);
										}}
									/>
									<Input
										type="number"
										defaultValue={paper.order}
										className="w-20"
										disabled={!paper.active}
										aria-label={`Thứ tự ${paper.name}`}
										onBlur={(e) => {
											const order = Number(e.target.value);
											if (!Number.isNaN(order) && order !== paper.order) {
												handleReorder(paper.id, order);
											}
										}}
									/>
									{!paper.active && <Badge variant="secondary">Đã vô hiệu hóa</Badge>}
									{/* No size="sm": these sit on a row with h-9 Inputs. */}
									{paper.active ? (
										<Button
											variant="outline"
											onClick={() => handleToggleActive(paper.id, false)}
											className="flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-400"
										>
											<Ban className="h-3 w-3" />
											Vô hiệu hóa
										</Button>
									) : (
										<Button
											variant="outline"
											onClick={() => handleToggleActive(paper.id, true)}
											className="flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-400"
										>
											<RotateCcw className="h-3 w-3" />
											Kích hoạt
										</Button>
									)}
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</div>

			{/* Per-method mapping */}
			<div className="space-y-4">
				<h3 className="text-lg font-semibold text-foreground">Hồ sơ theo diện xét tuyển</h3>
				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">
						Diện xét tuyển
					</label>
					<Select value={selectedMethodId} onChange={(e) => setSelectedMethodId(e.target.value)}>
						{ADMISSION_METHODS.map((method) => (
							<SelectItem key={method.id} value={method.id}>
								{method.name}
							</SelectItem>
						))}
					</Select>
				</div>

				{mappingError && <p className="text-sm text-red-600 dark:text-red-400">{mappingError}</p>}
				{methodPapersLoading ? (
					<div className="space-y-2">
						{Array.from({ length: 5 }).map((_, i) => (
							<div key={i} className="flex items-center gap-3">
								<Skeleton className="h-4 w-4 rounded-sm" />
								<Skeleton className="h-4 w-56" />
							</div>
						))}
					</div>
				) : (
					<div className="space-y-2">
						{activePapers.map((paper) => {
							const attached = methodPapers.find((mp) => mp.paperId === paper.id);
							const isAttached = attachedPaperIds.has(paper.id);
							return (
								<div key={paper.id} className="flex items-center gap-3 text-sm">
									<Checkbox
										id={`attach-${paper.id}`}
										checked={isAttached}
										onCheckedChange={(checked: boolean) => handleAttachToggle(paper.id, checked)}
									/>
									<label htmlFor={`attach-${paper.id}`} className="flex-1 cursor-pointer">
										{paper.name}
									</label>
									<label className="flex items-center gap-1 text-xs text-muted-foreground">
										<Checkbox
											id={`default-${paper.id}`}
											checked={attached?.defaultChecked ?? true}
											disabled={!isAttached}
											onCheckedChange={(checked: boolean) =>
												handleDefaultCheckedToggle(paper.id, checked)
											}
										/>
										mặc định tích sẵn
									</label>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
